/**
 * @fileoverview Reusable inline artwork renderer for catalog entries.
 * @module ui-artwork/components/ArtworkRenderer
 *
 * Responsibilities: Render a catalog SVG with accessible naming and optional
 *   workflow halo decoration.
 * Dependencies: buildArtworkSvg, React.
 * Architecture: Presentation-only component; it performs no data access.
 * Notes: Halo animation is opt-in and independent of the static artwork ID.
 */
import React from 'react';
import { buildArtworkSvg } from '../buildArtworkSvg';
import { getArtworkEntry } from '../catalog';

export interface ArtworkRendererProps {
  artworkId: string;
  color?: string;
  size?: number;
  surface?: 'marker' | 'row' | 'header';
  showHalo?: boolean;
  title?: string;
  className?: string;
}

/** @component ArtworkRenderer
 * Purpose: Render bundled artwork consistently across map, row, and header surfaces.
 * Props: ArtworkRendererProps.
 * Behavior: Builds inline SVG and displays an optional accessible halo ring.
 * Data Source: Static package catalog only.
 * Constraints: No remote assets, APIs, or data subscriptions.
 * Performance: Inline SVG generation is synchronous and bounded to one icon.
 * @reusable
 */
export function ArtworkRenderer({
  artworkId,
  color,
  size = 32,
  surface = 'row',
  showHalo = false,
  title,
  className,
}: ArtworkRendererProps) {
  const svg = buildArtworkSvg(artworkId, { color, size, plinth: surface === 'marker' });
  if (!svg) return null;
  return (
    <span
      className={`relative inline-flex shrink-0 items-center justify-center ${className ?? ''}`}
      data-artwork-surface={surface}
      style={{ width: size, height: size }}
    >
      <span role="img" aria-label={title ?? getArtworkEntry(artworkId)?.label ?? artworkId} dangerouslySetInnerHTML={{ __html: svg }} />
      {showHalo && (
        <span
          aria-hidden="true"
          data-testid="artwork-active-halo"
          className="pointer-events-none absolute inset-[-4px] rounded-full border-2 border-violet-600 animate-pulse"
        />
      )}
    </span>
  );
}

export default ArtworkRenderer;
